import React, { useState, useEffect, useRef } from 'react';
import pipelineService from '../services/pipelineService';
import userService from '../services/userService';
import { API_BASE_URL } from '../config/api';

/**
 * CardChat — team chat/comments on pipeline cards with @mention and #candidate autocomplete.
 */
const CardChat = ({ cardId }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  
  const [users, setUsers] = useState([]);
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionPosition, setMentionPosition] = useState(0);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  
  const [candidates, setCandidates] = useState([]);
  const [showCandidateDropdown, setShowCandidateDropdown] = useState(false);
  const [candidateSearch, setCandidateSearch] = useState('');
  const [candidatePosition, setCandidatePosition] = useState(0);
  const [selectedCandidateIndex, setSelectedCandidateIndex] = useState(0);
  
  const textareaRef = useRef(null);

  useEffect(() => {
    loadMessages();
    loadUsers();
    loadCandidates();
  }, [cardId]);

  const loadMessages = async () => {
    try {
      const msgs = await pipelineService.getChatMessages(cardId);
      setMessages(msgs);
      setError(null);
    } catch (err) {
      console.error('Error loading messages:', err);
      setError('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const userList = await userService.getAll();
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const firmId = currentUser.firm_id;
      setUsers(firmId ? userList.filter(u => u.firm_id === firmId) : userList);
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  const loadCandidates = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/api/candidates`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setCandidates(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error loading candidates:', err);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;
    setSending(true);
    try {
      await pipelineService.addChatMessage(cardId, newMessage);
      setNewMessage('');
      await loadMessages();
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send');
    } finally {
      setSending(false);
    }
  };

  const handleMessageChange = (e) => {
    const value = e.target.value;
    setNewMessage(value);
    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = value.substring(0, cursorPos);

    // Check for @mention
    const atMatch = textBeforeCursor.match(/@(\w*)$/);
    if (atMatch) {
      setMentionSearch(atMatch[1].toLowerCase());
      setMentionPosition(cursorPos - atMatch[0].length);
      setShowMentionDropdown(true);
      setSelectedMentionIndex(0);
      setShowCandidateDropdown(false);
      return;
    }

    // Check for #candidate
    const hashMatch = textBeforeCursor.match(/#(\w*)$/);
    if (hashMatch) {
      setCandidateSearch(hashMatch[1].toLowerCase());
      setCandidatePosition(cursorPos - hashMatch[0].length);
      setShowCandidateDropdown(true);
      setSelectedCandidateIndex(0);
      setShowMentionDropdown(false);
      return;
    }

    setShowMentionDropdown(false);
    setShowCandidateDropdown(false);
  };

  const insertMention = (user) => {
    const before = newMessage.substring(0, mentionPosition);
    const after = newMessage.substring(textareaRef.current?.selectionStart || mentionPosition);
    const remaining = after.replace(/^@\w*/, '');
    setNewMessage(`${before}@${user.name} ${remaining}`);
    setShowMentionDropdown(false);
    textareaRef.current?.focus();
  };

  const insertCandidate = (candidate) => {
    const before = newMessage.substring(0, candidatePosition);
    const after = newMessage.substring(textareaRef.current?.selectionStart || candidatePosition);
    const remaining = after.replace(/^#\w*/, '');
    setNewMessage(`${before}#${candidate.name} ${remaining}`);
    setShowCandidateDropdown(false);
    textareaRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (showMentionDropdown) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedMentionIndex(i => Math.min(i + 1, filteredUsers.length - 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedMentionIndex(i => Math.max(i - 1, 0)); }
      else if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); if (filteredUsers[selectedMentionIndex]) insertMention(filteredUsers[selectedMentionIndex]); }
      else if (e.key === 'Escape') { setShowMentionDropdown(false); }
      return;
    }
    if (showCandidateDropdown) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedCandidateIndex(i => Math.min(i + 1, filteredCandidates.length - 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedCandidateIndex(i => Math.max(i - 1, 0)); }
      else if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); if (filteredCandidates[selectedCandidateIndex]) insertCandidate(filteredCandidates[selectedCandidateIndex]); }
      else if (e.key === 'Escape') { setShowCandidateDropdown(false); }
      return;
    }
    // Ctrl/Cmd+Enter to send
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  const handleStartEdit = (msg) => { setEditingId(msg.id); setEditText(msg.message); };
  const handleCancelEdit = () => { setEditingId(null); setEditText(''); };

  const handleSaveEdit = async (messageId) => {
    if (!editText.trim()) return;
    try {
      await pipelineService.updateChatMessage(messageId, editText);
      setEditingId(null);
      setEditText('');
      await loadMessages();
    } catch (err) {
      console.error('Error updating message:', err);
    }
  };

  const handleDelete = async (messageId) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await pipelineService.deleteChatMessage(messageId);
      await loadMessages();
    } catch (err) {
      console.error('Error deleting message:', err);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const highlightMentions = (text) => {
    const mentionPattern = /(@|#)([A-Za-z]+\s+[A-Za-z]+)(?=\s|[.,!?;:]|$)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    while ((match = mentionPattern.exec(text)) !== null) {
      if (match.index > lastIndex) parts.push(text.substring(lastIndex, match.index));
      const symbol = match[1];
      const name = match[2];
      if (symbol === '@') {
        parts.push(<span key={match.index} className="text-emerald-700 font-medium bg-emerald-50 px-0.5 rounded text-[11px]">{match[0]}</span>);
      } else {
        const candidate = candidates.find(c => c.name === name);
        if (candidate) {
          parts.push(<a key={match.index} href={`/candidate/${candidate.id}`} onClick={(e) => { e.preventDefault(); window.location.href = `/candidate/${candidate.id}`; }}
            className="text-green-600 font-medium bg-green-50 px-0.5 rounded text-[11px] hover:bg-green-100 hover:underline cursor-pointer" title={`View ${candidate.name}`}>{match[0]}</a>);
        } else {
          parts.push(<span key={match.index} className="text-green-600 font-medium bg-green-50 px-0.5 rounded text-[11px]">{match[0]}</span>);
        }
      }
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) parts.push(text.substring(lastIndex));
    return parts.length > 0 ? parts : text;
  };

  const filteredUsers = users.filter(user => user.name.toLowerCase().includes(mentionSearch));
  const filteredCandidates = candidates.filter(c => c.name.toLowerCase().includes(candidateSearch));

  if (loading) return <div className="py-6 text-center"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-emerald-600 mx-auto"></div></div>;

  return (
    <div className="flex flex-col h-full" style={{ maxHeight: '500px' }}>
      {error && <p className="text-[10px] text-red-500 mb-2">{error}</p>}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto mb-3 space-y-3">
        {messages.length === 0 ? (
          <div className="text-center py-8">
            <svg className="w-7 h-7 text-gray-300 mx-auto mb-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <p className="text-xs text-gray-400">No messages yet</p>
            <p className="text-[10px] text-gray-300 mt-0.5">Start the conversation</p>
          </div>
        ) : (
          messages.map(msg => (
            <div key={msg.id} className="group flex gap-2">
              {/* Avatar */}
              {msg.user_avatar ? (
                <img src={msg.user_avatar} alt="" className="w-6 h-6 rounded-full flex-shrink-0 mt-0.5" />
              ) : (
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0 mt-0.5">
                  {msg.user_name?.charAt(0) || '?'}
                </div>
              )}

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-1.5">
                  <span className="text-xs font-semibold text-gray-900">{msg.user_name || 'Unknown'}</span>
                  <span className="text-[10px] text-gray-400">{formatTime(msg.created_at)}</span>
                  {msg.created_at !== msg.updated_at && <span className="text-[9px] text-gray-300 italic">edited</span>}
                </div>

                {editingId === msg.id ? (
                  <div className="mt-1 space-y-1.5">
                    <textarea value={editText} onChange={(e) => setEditText(e.target.value)}
                      className="w-full text-xs px-2.5 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 resize-none" rows="2" />
                    <div className="flex gap-1.5">
                      <button onClick={() => handleSaveEdit(msg.id)} className="text-[10px] px-2.5 py-1 bg-emerald-600 text-white rounded hover:bg-emerald-700 font-medium">Save</button>
                      <button onClick={handleCancelEdit} className="text-[10px] px-2.5 py-1 bg-gray-100 text-gray-600 rounded hover:bg-gray-200">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="text-xs text-gray-700 whitespace-pre-wrap break-words mt-0.5 leading-relaxed">{highlightMentions(msg.message)}</p>
                    <div className="flex gap-2 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => handleStartEdit(msg)} className="text-[10px] text-gray-400 hover:text-emerald-700">Edit</button>
                      <button onClick={() => handleDelete(msg.id)} className="text-[10px] text-gray-400 hover:text-red-500">Delete</button>
                    </div>
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Input */}
      <div className="border-t border-gray-200 pt-2.5">
        <form onSubmit={handleSendMessage}>
          <div className="relative">
            <textarea ref={textareaRef} value={newMessage} onChange={handleMessageChange} onKeyDown={handleKeyDown}
              placeholder="Message... (@ mention, # candidate)"
              className="w-full text-xs px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 resize-none bg-gray-50 focus:bg-white transition-colors"
              rows="2" disabled={sending} />

            {/* User mention dropdown */}
            {showMentionDropdown && (
              <div className="absolute bottom-full left-0 mb-1 w-64 bg-white border border-emerald-200 rounded-lg shadow-lg max-h-48 overflow-y-auto z-[100]">
                <div className="px-3 py-1.5 bg-emerald-50 border-b border-emerald-100 sticky top-0">
                  <span className="text-[10px] font-medium text-emerald-700">Team members {mentionSearch && `matching "${mentionSearch}"`}</span>
                </div>
                {filteredUsers.length === 0 ? (
                  <div className="px-3 py-3 text-xs text-gray-400 text-center">No users found</div>
                ) : filteredUsers.map((user, index) => (
                  <button key={user.id} type="button" onClick={() => insertMention(user)}
                    className={`w-full text-left px-3 py-2 flex items-center gap-2.5 hover:bg-emerald-50 border-b border-gray-50 last:border-0 ${index === selectedMentionIndex ? 'bg-emerald-50' : ''}`}>
                    {user.avatar_url ? (
                      <img src={user.avatar_url} alt="" className="w-6 h-6 rounded-full flex-shrink-0" />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">{user.name.charAt(0)}</div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-medium text-gray-900">{user.name}</div>
                      <div className="text-[10px] text-gray-500 truncate">{user.email}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Candidate mention dropdown */}
            {showCandidateDropdown && (
              <div className="absolute bottom-full left-0 mb-1 w-72 bg-white border border-green-200 rounded-lg shadow-lg max-h-48 overflow-y-auto z-[100]">
                <div className="px-3 py-1.5 bg-green-50 border-b border-green-100 sticky top-0">
                  <span className="text-[10px] font-medium text-green-700">Candidates {candidateSearch && `matching "${candidateSearch}"`}</span>
                </div>
                {filteredCandidates.length === 0 ? (
                  <div className="px-3 py-3 text-xs text-gray-400 text-center">No candidates found</div>
                ) : filteredCandidates.slice(0, 10).map((candidate, index) => (
                  <button key={candidate.id} type="button" onClick={() => insertCandidate(candidate)}
                    className={`w-full text-left px-3 py-2 flex items-center gap-2.5 hover:bg-green-50 border-b border-gray-50 last:border-0 ${index === selectedCandidateIndex ? 'bg-green-50' : ''}`}>
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">{candidate.name?.charAt(0) || '?'}</div>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-medium text-gray-900 truncate">{candidate.name}</div>
                      <div className="text-[10px] text-gray-500 truncate">{candidate.current_title || 'Candidate'}{candidate.current_company ? ` at ${candidate.current_company}` : ''}</div>
                    </div>
                  </button>
                ))}
                {filteredCandidates.length > 10 && (
                  <div className="px-3 py-1.5 text-[10px] text-gray-400 text-center bg-gray-50 border-t">+{filteredCandidates.length - 10} more — keep typing to narrow</div>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-between items-center mt-1.5">
            <span className="text-[9px] text-gray-400">@ mention · # candidate · Ctrl+Enter send</span>
            <button type="submit" disabled={!newMessage.trim() || sending}
              className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${!newMessage.trim() || sending ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-emerald-600 text-white hover:bg-emerald-700'}`}>
              {sending ? 'Sending...' : 'Send'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CardChat;
